/**
 * ChallengesSection — creator challenges, on whatever surface placed them.
 *
 * Two rendering modes:
 * 1. KING_OF_HILL - Queue-based 1v1 challenges (watch stream + join queue)
 * 2. CREATOR_VS_COMMUNITY - Jumbotron style (creator vs top community, leaderboard below)
 *
 * Architecture: competition umbrella → items[] (type: CHALLENGE) → CompetitionChallenge docs.
 *
 * NOT "for Hub pages", which is what this said while taking a `surfaceSlug` and
 * fetching `/hubs/slug/:slug/competitions` — so the Drop page passed a Drop
 * slug to it and got a 404, or another entity's challenges. It takes a
 * competition id now and knows nothing about who is rendering it. Falls back to
 * streaming API data for live status when available.
 */

import { ReactElement, useState, useMemo } from "react";
import {
    FaTrophy,
    FaGamepad,
    FaUsers,
    FaCrosshairs,
    FaTwitch,
    FaYoutube,
    FaPlay,
    FaClock,
    FaFire,
    FaCrown,
} from "react-icons/fa";
import { SiKick } from "react-icons/si";
import { StreamTheater, StreamData } from "@/widgets/StreamTheater";
import { useGetCompetitionItems, type SurfaceCompetitionResponse } from "@/hooks/useGetCompetitionItems";
import { useStreamViewTracking } from "@/hooks/useStreamViewTracking";
import { useGetCompetitionStreaming } from "@/hooks/useGetCompetitionStreaming";
import Skeleton from "react-loading-skeleton";
import "./ChallengesSection.scss";
import {useGetPaidEntryEligibility} from "@/competitions/hooks/useGetPaidEntryEligibility";

interface ChallengesSectionProps {
    /**
     * The competition umbrella this section renders. That is ALL it needs.
     *
     * It used to take `surfaceSlug` and fetch `/hubs/slug/:slug/competitions`, so
     * the Drop page passed a Drop slug to a prop named for a Hub — 404 when no
     * Hub shared the slug (the permanent "Failed to load challenges" on the
     * Call of Duty page), and somebody else's competitions when one did.
     *
     * Adding a `dropSlug` beside it would have been the same mistake twice: a
     * challenge does not belong to a surface type. It is PLACED on a surface,
     * the same way quests, missions, objectives and playable ads are, and the
     * next surface to carry one should need no change here at all. Every host
     * already stores `competitionId`; that id is the whole contract.
     */
    competitionId?: string;
    /** Hero video URL (from heroSection) */
    heroVideoUrl?: string;
    /** Hero image URL (fallback if no video) */
    heroImageUrl?: string;
    /** Name for the section title */
    name?: string;
    /** Host id, for stream-view tracking attribution only. */
    hostId?: string;
}

interface CreatorChallengeItem {
    id: string;
    creatorName: string;
    creatorAvatar: string | null;
    /*
     * Optional, because the underlying `FeaturedStreamResponse.platform` is —
     * platform is a property of a CHANNEL and a roster member need not have
     * one. Required here forced the fake `"TWITCH"` default below.
     */
    platform?: string;
    isLive: boolean;
    viewerCount: number;
    streamData: StreamData | null;
    challengeTitle: string;
    challengeType: string;
    entryFee: number;
    entryFeeType: string;
    prizeAmount: number;
    prizeType: string;
    queueSize: number;
    queueFilled: number;
    currentStreak: number;
    game: string;
    scheduledAt?: number;
    status?: string;
    timingMode?: string;
}

/*
 * Challenge types mapped by streamer name
 * This demo data provides challenge-specific info that the streaming API doesn't have
 */
/*
 * All challenges are FREE entry - sponsored by Monster Energy
 */
const CHALLENGE_METADATA: Record<string, {
    challengeTitle: string;
    challengeType: string;
    entryFee: number;
    entryFeeType: string;
    prizeAmount: number;
    prizeType: string;
    queueSize: number;
    queueFilled: number;
    currentStreak: number;
    game: string;
    scheduledAt?: number;
}> = {
    "symfuhny": {
        challengeTitle: "Beat Symfuhny - Warzone Gulag",
        challengeType: "1v1 Gulag",
        entryFee: 0,
        entryFeeType: "Free",
        prizeAmount: 1000,
        prizeType: "Beast Points",
        queueSize: 30,
        queueFilled: 22,
        currentStreak: 9,
        game: "Call of Duty: Warzone",
    },
    "aydan": {
        challengeTitle: "Aydan 1v1 Challenge",
        challengeType: "1v1 Gunfight",
        entryFee: 0,
        entryFeeType: "Free",
        prizeAmount: 750,
        prizeType: "Beast Points",
        queueSize: 25,
        queueFilled: 18,
        currentStreak: 7,
        game: "Call of Duty: Warzone",
    },
    "teepee": {
        challengeTitle: "TeePee Kill Race",
        challengeType: "Most Kills in 1 Game",
        entryFee: 0,
        entryFeeType: "Free",
        prizeAmount: 500,
        prizeType: "Beast Points",
        queueSize: 20,
        queueFilled: 12,
        currentStreak: 4,
        game: "Call of Duty: Warzone",
    },
    "huskerrs": {
        challengeTitle: "HusKerrs Sniper Challenge",
        challengeType: "Sniper 1v1",
        entryFee: 0,
        entryFeeType: "Free",
        prizeAmount: 1000,
        prizeType: "Beast Points",
        queueSize: 30,
        queueFilled: 0,
        currentStreak: 0,
        game: "Call of Duty: Warzone",
        scheduledAt: Date.now() + 3600000,
    },
    "rikimarue": { // Rogue's Twitch handle
        challengeTitle: "Rogue BO6 Ranked Play",
        challengeType: "Best of 3",
        entryFee: 0,
        entryFeeType: "Free",
        prizeAmount: 750,
        prizeType: "Beast Points",
        queueSize: 20,
        queueFilled: 0,
        currentStreak: 0,
        game: "Call of Duty: Black Ops 6",
        scheduledAt: Date.now() + 7200000,
    },
    "shotzzy": {
        challengeTitle: "Shotzzy CDL Challenge",
        challengeType: "Pro Rules 1v1",
        entryFee: 0,
        entryFeeType: "Free",
        prizeAmount: 1500,
        prizeType: "Beast Points",
        queueSize: 50,
        queueFilled: 0,
        currentStreak: 0,
        game: "Call of Duty: Black Ops 6",
        scheduledAt: Date.now() + 10800000,
    },
};

/* Default challenge data for streamers not in the metadata map - All Monster-sponsored challenges are FREE */
const DEFAULT_CHALLENGE = {
    challengeTitle: "Creator Challenge",
    challengeType: "1v1",
    entryFee: 0,
    entryFeeType: "Free",
    prizeAmount: 500,
    prizeType: "Beast Points",
    queueSize: 20,
    queueFilled: 0,
    currentStreak: 0,
    game: "Call of Duty: Warzone",
};


// Get platform icon
/* Optional — a creator may have no channel, so no platform. */
const getPlatformIcon = (platform?: string) => {
    switch (platform) {
        case "TWITCH":
            return <FaTwitch size={14} />;
        case "YOUTUBE":
            return <FaYoutube size={14} />;
        case "KICK":
            return <SiKick size={14} />;
        default:
            return <FaPlay size={14} />;
    }
};

/**
 * Format a timestamp as a human-readable relative time
 * @param scheduledAt - Unix timestamp in milliseconds
 * @returns Formatted string like "In 30 min", "In 2 hours", "Tomorrow", etc.
 */
function formatRelativeTime(scheduledAt: number | undefined): string {
    if (!scheduledAt) {
        return "Coming soon";
    }

    const now = Date.now();
    const diff = scheduledAt - now;

    // Already started or in the past
    if (diff <= 0) {
        return "Starting now";
    }

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
        return `In ${minutes} min`;
    }
    if (hours < 24) {
        return hours === 1 ? "In 1 hour" : `In ${hours} hours`;
    }
    if (days === 1) {
        return "Tomorrow";
    }
    return `In ${days} days`;
}

export const ChallengesSection = ({
    competitionId,
    heroVideoUrl: _heroVideoUrl,
    heroImageUrl: _heroImageUrl,
    name: _name = "Challenges",
    hostId,
}: ChallengesSectionProps): ReactElement => {
    const paidEntryEligibility = useGetPaidEntryEligibility();
    /* Paid entries only — a free one is unaffected by a location block. */
    const blockedLabel = paidEntryEligibility.blockedLabel;
    const [isTheaterOpen, setIsTheaterOpen] = useState(false);
    const [selectedStream, setSelectedStream] = useState<StreamData | null>(null);
    const { onTheaterOpen, onTheaterClose, elapsedSeconds } = useStreamViewTracking();

    /* CHALLENGE-type items in this umbrella. One request, no host in it. */
    const { data: competitionsData, isLoading, isError } = useGetCompetitionItems(competitionId, {
        type: "CHALLENGE",
    });

    // Fetch featured streamers from API (includes profileImageUrl, isLive, viewerCount)
    const { data: streamingSettings } = useGetCompetitionStreaming(competitionId);

    // Get real challenge data from the hub competitions endpoint
    const challenges = competitionsData?.competitions || [];

    // Group challenges by structure type
    const { kingOfHillChallenges, creatorVsCommunity } = useMemo(() => {
        const kingOfHill: SurfaceCompetitionResponse[] = [];
        const cvsCommunity: SurfaceCompetitionResponse[] = [];

        for (const challenge of challenges) {
            if (challenge.structure === "CREATOR_VS_COMMUNITY") {
                cvsCommunity.push(challenge);
            } else {
                // KING_OF_HILL, HEAD_TO_HEAD, FREE_FOR_ALL, etc. → queue-based UI
                kingOfHill.push(challenge);
            }
        }

        return { kingOfHillChallenges: kingOfHill, creatorVsCommunity: cvsCommunity };
    }, [challenges]);

    // Merge streaming API data (avatar, live status) with challenge data for queue-based challenges
    const creatorChallenges: CreatorChallengeItem[] = useMemo((): CreatorChallengeItem[] => {
        const featuredStreams = streamingSettings?.featuredStreams || [];

        // If we have real challenge data, use it as the base
        if (kingOfHillChallenges.length > 0) {
            return kingOfHillChallenges.map((challenge) => {
                // Try to find matching stream by creator ID
                const matchingStream = featuredStreams.find(s => s._id === challenge.creatorId || s.label.toLowerCase() === challenge.name?.toLowerCase());

                return {
                    id: challenge.id,
                    creatorName: challenge.name || "Creator Challenge",
                    creatorAvatar: matchingStream?.profileImageUrl || null,
                    /*
                     * NO FAKE TWITCH.
                     *
                     * This invented a platform when the creator genuinely has
                     * none — and it is rendered as literal text and as an icon
                     * further down, so a contestant who has never streamed was
                     * shown as being on Twitch. Exactly the fabrication that was
                     * just removed from the backend's add-member path; a
                     * required type is what forced it, so the type changed.
                     */
                    platform: matchingStream?.platform,
                    isLive: matchingStream?.isLive || challenge.status === "ACTIVE" || challenge.status === "LIVE",
                    viewerCount: matchingStream?.viewerCount || 0,
                    streamData: matchingStream ? {
                        _id: matchingStream._id,
                        label: matchingStream.label,
                        platform: matchingStream.platform,
                        twitchChannelId: matchingStream.twitchChannelId,
                        kickChannelSlug: matchingStream.kickChannelSlug,
                        youtubeChannelId: matchingStream.youtubeChannelId,
                        isLive: matchingStream.isLive,
                        viewerCount: matchingStream.viewerCount,
                        profileImageUrl: matchingStream.profileImageUrl,
                    /*
                     * No `as StreamData` here any more. The cast was silencing a
                     * real mismatch — `StreamData.platform` was required while
                     * the source field is optional — and it is what let
                     * `undefined` reach `.toLowerCase()` in the theater. The
                     * shapes genuinely agree now, so the assertion has nothing
                     * left to assert and would only hide the next drift.
                     */
                    } : null,
                    challengeTitle: challenge.name || "Creator Challenge",
                    challengeType: challenge.structure || "1v1",
                    entryFee: 0,
                    entryFeeType: challenge.entryType || "Free",
                    prizeAmount: challenge.prizePool?.total || 0,
                    prizeType: challenge.prizePool?.currency || "FUEL",
                    queueSize: challenge.maxParticipants || 20,
                    queueFilled: challenge.participantCount || 0,
                    currentStreak: 0,
                    game: challenge.game?.name || "Unknown Game",
                    scheduledAt: challenge.startDate,
                    status: challenge.status,
                    timingMode: challenge.timingMode,
                };
            });
        }

        // Fallback to streaming API + demo metadata when no real challenges
        return featuredStreams.map((stream) => {
            const channelId = (stream.twitchChannelId || stream.label || "").toLowerCase();
            const metadata = CHALLENGE_METADATA[channelId] || DEFAULT_CHALLENGE;

            return {
                id: stream._id,
                creatorName: stream.label,
                creatorAvatar: stream.profileImageUrl || null,
                platform: stream.platform,
                isLive: stream.isLive,
                viewerCount: stream.viewerCount,
                streamData: {
                    _id: stream._id,
                    label: stream.label,
                    platform: stream.platform,
                    twitchChannelId: stream.twitchChannelId,
                    kickChannelSlug: stream.kickChannelSlug,
                    youtubeChannelId: stream.youtubeChannelId,
                    isLive: stream.isLive,
                    viewerCount: stream.viewerCount,
                    profileImageUrl: stream.profileImageUrl,
                },
                ...metadata,
            };
        });
    }, [streamingSettings, kingOfHillChallenges]);

    // Handler to open theater mode for a creator
    const handleWatchStream = (streamData: StreamData) => {
        setSelectedStream(streamData);
        setIsTheaterOpen(true);
        onTheaterOpen({
            platform: streamData.platform,
            channelId: streamData.twitchChannelId || streamData.kickChannelSlug || streamData.youtubeChannelId || "",
            surfaceId: hostId,
            competitionId,
        });
    };

    // Partition challenges into live vs upcoming in a single pass
    const { liveCreatorChallenges, upcomingCreatorChallenges } = useMemo(() => {
        const live: typeof creatorChallenges = [];
        const upcoming: typeof creatorChallenges = [];

        for (const challenge of creatorChallenges) {
            if (challenge.isLive) {
                live.push(challenge);
            } else {
                upcoming.push(challenge);
            }
        }

        return { liveCreatorChallenges: live, upcomingCreatorChallenges: upcoming };
    }, [creatorChallenges]);

    // Loading state
    if (isLoading) {
        return (
            <section className="challenges-section">
                <div className="challenges-section__loading">
                    <Skeleton height={100} />
                    <div className="challenges-section__creator-grid">
                        <Skeleton height={280} />
                        <Skeleton height={280} />
                        <Skeleton height={280} />
                    </div>
                </div>
            </section>
        );
    }

    /*
     * Nothing to show ⇒ show nothing.
     *
     * This read as a failure on any Drop without competitions: the query hits
     * the HUB endpoint `/hubs/slug/:slug/competitions` with a DROP slug, and a
     * Drop with no same-slug Hub 404s — so the Call of Duty page rendered
     * "Failed to load challenges. Please try again later." under a heading,
     * permanently, for a Drop that simply has no challenge attached.
     *
     * Every other section on the page is silent when empty. A section that
     * announces an error for the ordinary case trains an operator to ignore
     * the one time it means something.
     */
    if (isError) {
        return null;
    }

    return (
        <section className="challenges-section">
            {/* Creator vs Community Jumbotron Section */}
            {creatorVsCommunity.length > 0 &&
                <div className="challenges-section__jumbotron-section">
                    {creatorVsCommunity.map((challenge) =>
                        <div key={challenge.id} className="challenges-section__jumbotron">
                            <div className="challenges-section__jumbotron-header">
                                <h2>{challenge.name}</h2>
                                {challenge.status === "ACTIVE" || challenge.status === "LIVE"
                                    ? <span className="challenges-section__live-badge">
                                        <span className="challenges-section__live-dot" /> LIVE
                                    </span>
                                    : <span className="challenges-section__upcoming-badge">
                                        <FaClock size={12} /> {challenge.status}
                                    </span>
                                }
                            </div>

                            <div className="challenges-section__jumbotron-vs">
                                {/* Creator Side */}
                                <div className="challenges-section__jumbotron-player challenges-section__jumbotron-player--creator">
                                    <div className="challenges-section__jumbotron-avatar">
                                        <FaCrown size={20} className="challenges-section__crown" />
                                        <FaUsers size={48} />
                                    </div>
                                    <span className="challenges-section__jumbotron-label">CREATOR</span>
                                    <span className="challenges-section__jumbotron-name">
                                        {challenge.name?.split(" ")[0] || "Creator"}
                                    </span>
                                    <span className="challenges-section__jumbotron-score">0</span>
                                </div>

                                {/* VS Badge */}
                                <div className="challenges-section__jumbotron-vs-badge">
                                    <span>VS</span>
                                </div>

                                {/* Community Leader Side */}
                                <div className="challenges-section__jumbotron-player challenges-section__jumbotron-player--community">
                                    <div className="challenges-section__jumbotron-avatar">
                                        <FaTrophy size={20} className="challenges-section__trophy" />
                                        <FaUsers size={48} />
                                    </div>
                                    <span className="challenges-section__jumbotron-label">#1 COMMUNITY</span>
                                    <span className="challenges-section__jumbotron-name">Top Player</span>
                                    <span className="challenges-section__jumbotron-score">0</span>
                                </div>
                            </div>

                            {/* Community Leaderboard */}
                            <div className="challenges-section__jumbotron-leaderboard">
                                <h3><FaTrophy size={14} /> Community Leaderboard</h3>
                                <div className="challenges-section__jumbotron-leaderboard-list">
                                    {/* Placeholder - will be populated with real leaderboard data */}
                                    <div className="challenges-section__jumbotron-leaderboard-empty">
                                        <p>Be the first to challenge the creator!</p>
                                        <button className="challenges-section__btn challenges-section__btn--primary">
                                            JOIN CHALLENGE
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Challenge Info */}
                            <div className="challenges-section__jumbotron-info">
                                {challenge.game &&
                                    <span><FaGamepad size={12} /> {challenge.game.name}</span>
                                }
                                {challenge.prizePool &&
                                    <span><FaTrophy size={12} /> {challenge.prizePool.total} {challenge.prizePool.currency || "Points"}</span>
                                }
                                {challenge.participantCount !== undefined &&
                                    <span><FaUsers size={12} /> {challenge.participantCount} challengers</span>
                                }
                            </div>
                        </div>)}
                </div>
            }

            {/* Queue-based Challenges (KING_OF_HILL, HEAD_TO_HEAD, etc.) —
              * explained only when some actually exist; an explainer over an
              * absent feature is a heading over nothing. */}
            {creatorChallenges.length > 0 &&
                <div className="challenges-section__creator-content">
                    {/* How It Works */}
                    <div className="challenges-section__how-it-works">
                        <h3>How Creator Challenges Work</h3>
                        <div className="challenges-section__how-it-works-grid">
                            <div className="challenges-section__how-it-works-item">
                                <span className="challenges-section__how-it-works-num">1</span>
                                <span>Watch a creator's live stream</span>
                            </div>
                            <div className="challenges-section__how-it-works-item">
                                <span className="challenges-section__how-it-works-num">2</span>
                                <span>Join the challenge queue</span>
                            </div>
                            <div className="challenges-section__how-it-works-item">
                                <span className="challenges-section__how-it-works-num">3</span>
                                <span>Play when it's your turn</span>
                            </div>
                            <div className="challenges-section__how-it-works-item">
                                <span className="challenges-section__how-it-works-num">4</span>
                                <span>Win to earn rewards!</span>
                            </div>
                        </div>
                    </div>

                    {/* Live Creator Challenges */}
                    {liveCreatorChallenges.length > 0 &&
                        <div className="challenges-section__live-section">
                            <h2 className="challenges-section__section-title">
                                <span className="challenges-section__live-dot" />
                                LIVE NOW
                            </h2>
                            <div className="challenges-section__creator-grid">
                                {liveCreatorChallenges.map((challenge) => {
                                    return (
                                        <div key={challenge.id} className="challenges-section__creator-card challenges-section__creator-card--live">
                                            <div className="challenges-section__creator-header">
                                                <div className="challenges-section__creator-info">
                                                    <div className="challenges-section__creator-avatar">
                                                        {challenge.creatorAvatar
                                                            ? <img src={challenge.creatorAvatar} alt={challenge.creatorName} />
                                                            : <FaUsers size={24} />
                                                        }
                                                    </div>
                                                    <div>
                                                        <span className="challenges-section__creator-name">
                                                            {challenge.creatorName}
                                                        </span>
                                                        <span className="challenges-section__creator-platform">
                                                            {getPlatformIcon(challenge.platform)}
                                                            {challenge.viewerCount.toLocaleString()} watching
                                                        </span>
                                                    </div>
                                                </div>
                                                <span className="challenges-section__live-badge">
                                                    <span className="challenges-section__live-dot" /> LIVE
                                                </span>
                                            </div>

                                            <div className="challenges-section__challenge-details">
                                                <h3 className="challenges-section__challenge-title">
                                                    {challenge.challengeTitle}
                                                </h3>
                                                <div className="challenges-section__challenge-meta">
                                                    <span><FaGamepad size={12} /> {challenge.game}</span>
                                                    <span><FaCrosshairs size={12} /> {challenge.challengeType}</span>
                                                </div>
                                            </div>

                                            <div className="challenges-section__challenge-rewards">
                                                <div className="challenges-section__reward-item">
                                                    <span className="challenges-section__reward-label">Entry</span>
                                                    <span className="challenges-section__reward-value">
                                                        {challenge.entryFee === 0
                                                            ? "FREE"
                                                            : paidEntryEligibility.isBlocked
                                                                ? "—"
                                                                : `${challenge.entryFee} ${challenge.entryFeeType}`}
                                                    </span>
                                                </div>
                                                <div className="challenges-section__reward-item">
                                                    <span className="challenges-section__reward-label">Prize</span>
                                                    <span className="challenges-section__reward-value challenges-section__reward-value--prize">
                                                        {challenge.prizeType === "USD" ? "$" : ""}{challenge.prizeAmount} {challenge.prizeType !== "USD" ? challenge.prizeType : ""}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="challenges-section__challenge-queue">
                                                <div className="challenges-section__queue-bar">
                                                    <div
                                                        className="challenges-section__queue-fill"
                                                        style={{ width: `${challenge.queueFilled / challenge.queueSize * 100}%` }}
                                                    />
                                                </div>
                                                <span className="challenges-section__queue-text">
                                                    {challenge.queueFilled}/{challenge.queueSize} in queue
                                                </span>
                                                {challenge.currentStreak > 0 &&
                                                <span className="challenges-section__streak-badge">
                                                    <FaFire size={12} /> {challenge.currentStreak} win streak
                                                </span>
                                                }
                                            </div>

                                            <div className="challenges-section__challenge-actions">
                                                <button
                                                    className="challenges-section__btn challenges-section__btn--primary"
                                                    disabled={paidEntryEligibility.isBlocked && challenge.entryFee !== 0}
                                                >
                                                    {paidEntryEligibility.isBlocked && challenge.entryFee !== 0
                                                        ? blockedLabel
                                                        : "JOIN QUEUE"}
                                                </button>
                                                {challenge.streamData !== null &&
                                                    <button
                                                        className="challenges-section__btn challenges-section__btn--secondary"
                                                        onClick={() => {
                                                            // Extract to const for proper TypeScript narrowing
                                                            const streamData = challenge.streamData;
                                                            if (streamData) {
                                                                handleWatchStream(streamData);
                                                            }
                                                        }}
                                                    >
                                                    WATCH STREAM
                                                    </button>
                                                }
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    }

                    {/* Upcoming Creator Challenges */}
                    {upcomingCreatorChallenges.length > 0 &&
                        <div className="challenges-section__upcoming-section">
                            <h2 className="challenges-section__section-title">
                                <FaClock size={16} /> UPCOMING
                            </h2>
                            <div className="challenges-section__creator-grid">
                                {upcomingCreatorChallenges.map((challenge) => {
                                    return (
                                        <div key={challenge.id} className="challenges-section__creator-card challenges-section__creator-card--upcoming">
                                            <div className="challenges-section__creator-header">
                                                <div className="challenges-section__creator-info">
                                                    <div className="challenges-section__creator-avatar">
                                                        {challenge.creatorAvatar
                                                            ? <img src={challenge.creatorAvatar} alt={challenge.creatorName} />
                                                            : <FaUsers size={24} />
                                                        }
                                                    </div>
                                                    <div>
                                                        <span className="challenges-section__creator-name">
                                                            {challenge.creatorName}
                                                        </span>
                                                        <span className="challenges-section__creator-platform">
                                                            {getPlatformIcon(challenge.platform)}
                                                            {challenge.platform}
                                                        </span>
                                                    </div>
                                                </div>
                                                <span className="challenges-section__upcoming-badge">
                                                    <FaClock size={12} /> {formatRelativeTime(challenge.scheduledAt)}
                                                </span>
                                            </div>

                                            <div className="challenges-section__challenge-details">
                                                <h3 className="challenges-section__challenge-title">
                                                    {challenge.challengeTitle}
                                                </h3>
                                                <div className="challenges-section__challenge-meta">
                                                    <span><FaGamepad size={12} /> {challenge.game}</span>
                                                    <span><FaCrosshairs size={12} /> {challenge.challengeType}</span>
                                                </div>
                                            </div>

                                            <div className="challenges-section__challenge-rewards">
                                                <div className="challenges-section__reward-item">
                                                    <span className="challenges-section__reward-label">Entry</span>
                                                    <span className="challenges-section__reward-value">
                                                        {challenge.entryFee === 0
                                                            ? "FREE"
                                                            : paidEntryEligibility.isBlocked
                                                                ? "—"
                                                                : `${challenge.entryFee} ${challenge.entryFeeType}`}
                                                    </span>
                                                </div>
                                                <div className="challenges-section__reward-item">
                                                    <span className="challenges-section__reward-label">Prize</span>
                                                    <span className="challenges-section__reward-value challenges-section__reward-value--prize">
                                                        {challenge.prizeType === "USD" ? "$" : ""}{challenge.prizeAmount} {challenge.prizeType !== "USD" ? challenge.prizeType : ""}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="challenges-section__challenge-actions">
                                                <button className="challenges-section__btn challenges-section__btn--secondary">
                                                SET REMINDER
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    }
                </div>
            }

            {/* Stream Theater Modal */}
            <StreamTheater
                stream={selectedStream}
                isOpen={isTheaterOpen}
                onClose={() => {
                    setIsTheaterOpen(false);
                    setSelectedStream(null);
                    onTheaterClose();
                }}
                showViewerCounts={true}
                availableStreams={liveCreatorChallenges
                    .filter(c => c.streamData !== null)
                    .map(c => c.streamData as StreamData)}
                onSwitchStream={(stream) => {
                    setSelectedStream(stream);
                    onTheaterOpen({
                        platform: stream.platform,
                        channelId: stream.twitchChannelId || stream.kickChannelSlug || stream.youtubeChannelId || "",
                        surfaceId: hostId,
                        competitionId,
                    });
                }}
                challenges={
                    selectedStream
                        ? liveCreatorChallenges
                            .filter(c => c.streamData?._id === selectedStream._id)
                            .map(c => ({
                                id: c.id,
                                creatorName: c.creatorName,
                                challengeTitle: c.challengeTitle,
                                challengeType: c.challengeType,
                                game: c.game,
                                entryFee: c.entryFee,
                                entryFeeType: c.entryFeeType,
                                prizeAmount: c.prizeAmount,
                                prizeType: c.prizeType,
                                queueSize: c.queueSize,
                                queueFilled: c.queueFilled,
                                currentStreak: c.currentStreak,
                            }))
                        : []
                }
                onJoinQueue={(_challenge) => {
                    // Queue join handled by ArenaQueueOverlay when challenge type is QUICK_MATCH
                }}
                elapsedSeconds={elapsedSeconds}
            />
        </section>
    );
};
